// client/src/pages/seo/DigitalMenuComparisonEn.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "What kind of digital menu does your restaurant need?",
  },
  {
    id: "definition",
    label: "What does digital menu comparison mean?",
  },
  {
    id: "criteria",
    label: "The most important comparison criteria",
  },
  {
    id: "provider-types",
    label: "What types of digital menu providers exist?",
  },
  {
    id: "pricing",
    label: "Compare costs: free, cheap, or useful long term?",
  },
  {
    id: "features",
    label: "Which functions really matter?",
  },
  {
    id: "seo",
    label: "Why visibility and a restaurant page matter in comparison",
  },
  {
    id: "mistakes",
    label: "Common mistakes when comparing digital menus",
  },
  {
    id: "bestplace",
    label: "Where Best Place fits in this comparison",
  },
  {
    id: "faq",
    label: "FAQ about digital menu comparison",
  },
];

const checklist = [
  "You are comparing several digital menu providers.",
  "You do not want to choose only by the lowest price.",
  "You need QR code, online menu, or mobile menu access.",
  "You want to edit prices, photos, and availability yourself.",
  "You may need reservation or ordering features later.",
  "You want to know whether a free plan is enough to start.",
  "You need a solution that works well for guests on smartphones.",
  "You want to depend less on PDFs, printing, or manual updates.",
  "You want to connect restaurant page, digital menu, and QR code long term.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "A very simple provider may be enough",
      text:
        "If you only want to show a small menu online, a simple QR or menu solution may be enough. Still, make sure the menu works well on smartphones.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Do not compare only by price",
      text:
        "For your restaurant, several criteria matter: editing, QR code, mobile view, photos, availability, and possible future extensions.",
      tone: "medium",
    };
  }

  return {
    title: "You probably need an extendable solution",
    text:
      "Several points suggest that your restaurant should choose a provider that does more than display a menu: management, QR code, website, reservations, and future extensions matter.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalMenuComparisonEn() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Digital Menu Providers Compared: What Restaurants Should Look For | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/digital-menu-comparison",
          de: "/de/digitale-speisekarte-vergleich",
          no: "/no/digital-meny-sammenligning",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digital menu providers compared: what restaurants should really
              look for
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                The best digital menu is not automatically the cheapest one or
                the one with the longest feature list. What matters is whether it
                helps in real restaurant work: menu updates, QR access, price
                changes, guest information, and future growth.
              </p>

              <p className="seoHero__text">
                This guide explains how restaurants can compare digital menu
                providers, which criteria matter, and why a digital menu should
                be more than just a PDF behind a QR code.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What kind of digital menu does your restaurant need?</h2>

            <p>
              Press the statements that sound true for your restaurant. The
              result is intentionally honest: sometimes a simple QR menu is
              enough, but many restaurants need a solution that can grow with
              menu management, reservation, website and future functions.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>What does digital menu comparison mean?</h2>

          <p>
            Comparing digital menus should not only mean asking: “Which provider
            is the cheapest?” The better question is: which solution fits your
            restaurant, your team, your guests, and your future plans?
          </p>

          <p>
            A digital menu can mean many things: a PDF with QR code, a simple
            mobile menu page, or a broader restaurant platform with menu, QR
            code, reservation, multilingual content, and future ordering
            options.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Mobile view.</li>
            <li className="text__seo-li">QR code access.</li>
            <li className="text__seo-li">Easy menu management.</li>
            <li className="text__seo-li">Photos and descriptions.</li>
            <li className="text__seo-li">Prices and availability.</li>
            <li className="text__seo-li">Multilingual support.</li>
            <li className="text__seo-li">Reservation.</li>
            <li className="text__seo-li">Website or restaurant page.</li>
            <li className="text__seo-li">Future extensions.</li>
          </ul>

          <h3>Compare the workflow, not only the price</h3>

          <p>
            A cheap tool can become frustrating if the menu is hard to update,
            the QR code flow is unclear, or guests struggle on smartphones. A
            useful comparison should always include real restaurant work.
          </p>
        </section>

        <section id="criteria" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>The most important comparison criteria</h2>

            <p>
              A good digital menu does not need every possible feature. But it
              should answer the important practical questions clearly.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Core criteria</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Mobile usage:</strong> Does the menu open quickly
                    and clearly on a smartphone?
                  </li>
                  <li className="text__seo-li">
                    <strong>Menu management:</strong> Can prices, photos, and
                    availability be changed easily?
                  </li>
                  <li className="text__seo-li">
                    <strong>QR code:</strong> Is the QR code easy to use, test,
                    and print?
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Growth criteria</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Design:</strong> Does the menu feel professional and
                    suitable for the restaurant?
                  </li>
                  <li className="text__seo-li">
                    <strong>Scalability:</strong> Can reservation, website, or
                    ordering be added later?
                  </li>
                  <li className="text__seo-li">
                    <strong>Pricing:</strong> Is it clear what is free and when
                    an upgrade becomes necessary?
                  </li>
                </ul>
              </div>
            </div>

            <p>
              The best provider is not always the one with the longest feature
              list. The best provider is the one your restaurant actually uses.
            </p>
          </div>
        </section>

        <section id="provider-types" className="acquaintance">
          <h2>What types of digital menu providers exist?</h2>

          <p>
            Digital menus come in several forms. To compare fairly, you first
            need to understand what type of solution you are looking at.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>PDF with QR code</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Fast start.</li>
                <li className="text__seo-li">Often cheap or free.</li>
                <li className="text__seo-li">
                  Less comfortable on smartphones.
                </li>
                <li className="text__seo-li">
                  Difficult when changes happen often.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col">
              <h3>Simple menu page</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Better mobile experience.</li>
                <li className="text__seo-li">
                  Categories and products are possible.
                </li>
                <li className="text__seo-li">Good for small restaurants.</li>
                <li className="text__seo-li">Often limited extensions.</li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Restaurant platform</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Menu, QR code, and restaurant page connected.
                </li>
                <li className="text__seo-li">
                  Reservation or ordering can be added later.
                </li>
                <li className="text__seo-li">
                  Better for long-term development.
                </li>
                <li className="text__seo-li">
                  Must still remain easy to use.
                </li>
              </ul>
            </div>
          </div>

          <VisualIdea>
            Three comparison cards on a restaurant owner’s desk: PDF with QR
            code, simple mobile menu page and full restaurant platform.
          </VisualIdea>
        </section>

        <section id="pricing" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Compare costs: free, cheap, or useful long term?</h2>

            <p>
              Many restaurants first search for a free digital menu. That makes
              sense. A free plan can be a strong starting point when you want to
              test whether a QR menu and online management work in real daily
              operations.
            </p>

            <p>
              Still, the entry price is not the only thing that matters. It is
              also important what happens when the restaurant grows or needs more
              features.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">What is free?</li>
              <li className="text__seo-li">What costs extra later?</li>
              <li className="text__seo-li">Is QR code included?</li>
              <li className="text__seo-li">Is menu management included?</li>
              <li className="text__seo-li">Is reservation possible?</li>
              <li className="text__seo-li">Is there a restaurant page?</li>
              <li className="text__seo-li">
                Is multilingual content possible?
              </li>
              <li className="text__seo-li">Can the system grow later?</li>
            </ul>

            <div className="seoCompareSmall">
              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/free-digital-menu-for-restaurants">
                    Free digital menu
                  </Link>
                </p>
              </div>

              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/manage-menu-online-for-restaurants">
                    Manage menu online
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="acquaintance">
          <h2>Which functions really matter?</h2>

          <p>
            Not every function is important for every restaurant. A small café
            may first need only a QR menu. A restaurant with reservations,
            terrace seating, and changing offers may need more structure.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Show menu online.</li>
            <li className="text__seo-li">Use QR code.</li>
            <li className="text__seo-li">Edit prices.</li>
            <li className="text__seo-li">Hide dishes.</li>
            <li className="text__seo-li">Add reservation.</li>
            <li className="text__seo-li">Expand later.</li>
          </ol>

          <p>
            The order matters: the foundation must work first. Then additional
            functions can be added with purpose.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Related page</strong>
              <p>
                <Link to="/en/digital-menu-for-restaurants">
                  Digital menu
                </Link>
              </p>
            </div>

            <div>
              <strong>Related page</strong>
              <p>
                <Link to="/en/online-table-reservation-for-restaurants">
                  Online table reservation
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="seo" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why visibility and a restaurant page matter in comparison</h2>

            <p>
              A digital menu is not important only for guests at the table. It
              can also help before the visit: when guests search online, check
              prices, view photos, or decide whether to reserve.
            </p>

            <p>
              This is why comparison should include one more question: does the
              provider offer only a menu link, or can it become part of a better
              digital presence for the restaurant?
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Only menu link</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Useful mainly after the guest has the QR code.
                  </li>
                  <li className="text__seo-li">
                    Limited support for discovery.
                  </li>
                  <li className="text__seo-li">
                    Restaurant information may stay scattered.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Menu + restaurant page</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Menu can support online discovery.
                  </li>
                  <li className="text__seo-li">
                    Restaurant information becomes clearer.
                  </li>
                  <li className="text__seo-li">
                    Reservation and contact can be connected.
                  </li>
                </ul>
              </div>
            </div>

            <div className="seoCompareSmall">
              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/restaurant-website-for-restaurants">
                    Restaurant website
                  </Link>
                </p>
              </div>

              <div>
                <strong>Related page</strong>
                <p>
                  <Link to="/en/restaurant-digitalization">
                    Restaurant digitalization
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="mistakes" className="acquaintance">
          <h2>Common mistakes when comparing digital menus</h2>

          <p>
            Comparison is useful only when it is practical. A provider can look
            good on a pricing table, but still be the wrong fit for real
            restaurant work.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Common mistakes</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Looking only at the lowest price.
                </li>
                <li className="text__seo-li">
                  Confusing a PDF with a real digital menu.
                </li>
                <li className="text__seo-li">
                  Not testing the mobile view.
                </li>
                <li className="text__seo-li">
                  Underestimating menu management.
                </li>
                <li className="text__seo-li">
                  Not asking future questions.
                </li>
                <li className="text__seo-li">
                  Trusting reviews without testing the tool.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Better</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Test the workflow with your own menu.
                </li>
                <li className="text__seo-li">
                  Check the smartphone experience.
                </li>
                <li className="text__seo-li">
                  Change a price and hide a dish.
                </li>
                <li className="text__seo-li">
                  Check QR code and restaurant page.
                </li>
                <li className="text__seo-li">
                  Ask whether reservation or ordering can be added later.
                </li>
                <li className="text__seo-li">
                  Compare usefulness, not only feature lists.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Frequently asked questions about digital menu comparison</h2>

          <div className="seoFaq">
            <details>
              <summary>What is the best digital menu?</summary>
              <p>
                The best solution depends on the restaurant. A small café needs
                different features than a restaurant with reservations, terrace
                seating, multiple languages, or changing offers.
              </p>
            </details>

            <details>
              <summary>Is a free provider enough?</summary>
              <p>
                A free provider can be enough for the beginning. The important
                question is whether you can realistically test menu management,
                QR code access, and mobile view.
              </p>
            </details>

            <details>
              <summary>Is a PDF with QR code enough?</summary>
              <p>
                For a very simple start, it can be enough. If you want to update
                prices, photos, daily offers, or availability, an editable online
                menu is usually better.
              </p>
            </details>

            <details>
              <summary>What should I check first?</summary>
              <p>
                Mobile view, easy editing, QR code, current prices, photos,
                availability, and future scalability are especially important.
              </p>
            </details>

            <details>
              <summary>Do I need a large platform immediately?</summary>
              <p>
                No. Starting small is often better. But the solution should be
                able to grow if reservation, multilingual content, or ordering
                becomes important later.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="business"
          exclude="/en/digital-menu-comparison"
        />
      </article>
    </main>
  );
}